import {useEffect, useState} from 'react';
import {authService, type UserInfo} from '@/services/authService';

interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserInfo | null;
}

/**
 * Hook for managing authentication state.
 * Checks for valid JWT token and fetches user information.
 */
export const useAuth = (): AuthState => {
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        isLoading: true,
        user: null,
    });

    useEffect(() => {
        // Check for authentication token or session
        const checkAuth = async () => {
            try {
                // Check if user is authenticated via stored token
                if (authService.isAuthenticated()) {
                    // Try to get user from token first (faster)
                    const userFromToken = authService.getUserFromToken();

                    if (userFromToken) {
                        setAuthState({
                            isAuthenticated: true,
                            isLoading: false,
                            user: userFromToken,
                        });

                        // Then fetch fresh user data from server
                        try {
                            const freshUser = await authService.getCurrentUser();
                            setAuthState({
                                isAuthenticated: true,
                                isLoading: false,
                                user: freshUser,
                            });
                        } catch (error) {
                            // If fetching fresh data fails, keep token data
                            console.warn('Failed to fetch fresh user data:', error);
                        }
                    } else {
                        // Token exists but can't parse user data, try fetching from server
                        const user = await authService.getCurrentUser();
                        setAuthState({
                            isAuthenticated: true,
                            isLoading: false,
                            user,
                        });
                    }
                } else {
                    // No valid token
                    // For development, allow mock authentication
                    if (process.env.NODE_ENV === 'development' && import.meta.env.VITE_MOCK_AUTH === 'true') {
                        setAuthState({
                            isAuthenticated: true,
                            isLoading: false,
                            user: {
                                id: 1,
                                username: 'dev_user',
                                email: 'dev@example.com',
                                full_name: 'Development User',
                                role: 'admin',
                                is_active: true,
                                last_login: null,
                                created_at: new Date().toISOString(),
                            },
                        });
                    } else {
                        setAuthState({
                            isAuthenticated: false,
                            isLoading: false,
                            user: null,
                        });
                    }
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                setAuthState({
                    isAuthenticated: false,
                    isLoading: false,
                    user: null,
                });
            }
        };

        checkAuth();
    }, []);

    return authState;
};
